import React, { useState, useRef, useEffect } from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import es from 'date-fns/locale/es'
import { Button } from 'react-bootstrap'
import { getServerDate, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addDays } from 'utils/date'
import 'react-datepicker/dist/react-datepicker.css'

// Registrar locale español
registerLocale('es', es)

const PRESETS = [
	{ key: 'hoy', label: 'Hoy' },
	{ key: 'ayer', label: 'Ayer' },
	{ key: 'estaSemana', label: 'Esta semana' },
	{ key: 'semanaAnterior', label: 'Semana anterior' },
	{ key: 'esteMes', label: 'Este mes' },
	{ key: 'mesAnterior', label: 'Mes pasado' },
	{ key: 'personalizado', label: 'Personalizado' }
]

const getPresetDates = (presetKey) => {
	const now = new Date()

	switch (presetKey) {
		case 'hoy':
			return { desde: startOfDay(now), hasta: endOfDay(now) }
		case 'ayer':
			return { desde: startOfDay(addDays(-1, now)), hasta: endOfDay(addDays(-1, now)) }
		case 'estaSemana':
			return { desde: startOfWeek(now), hasta: endOfWeek(now) }
		case 'semanaAnterior':
			const lastWeek = addDays(-7, now)
			return { desde: startOfWeek(lastWeek), hasta: endOfWeek(lastWeek) }
		case 'esteMes':
			return { desde: startOfMonth(now), hasta: endOfMonth(now) }
		case 'mesAnterior':
			const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
			return { desde: startOfMonth(lastMonth), hasta: endOfMonth(lastMonth) }
		default:
			return null
	}
}

const formatDisplayDate = (date) => {
	if (!date) return ''
	const options = { day: 'numeric', month: 'short', year: 'numeric' }
	return date.toLocaleDateString('es-ES', options)
}

const DateRangeSelector = ({
	fechaInicio,
	fechaFin,
	onRangeChange,
	onConsultar
}) => {
	const [activePreset, setActivePreset] = useState('esteMes')
	const [showCalendar, setShowCalendar] = useState(false)
	const [tempDesde, setTempDesde] = useState(fechaInicio)
	const [tempHasta, setTempHasta] = useState(fechaFin)
	const calendarRef = useRef(null)

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (calendarRef.current && !calendarRef.current.contains(event.target)) {
				setShowCalendar(false)
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	const handlePresetClick = (presetKey) => {
		setActivePreset(presetKey)

		if (presetKey === 'personalizado') {
			setShowCalendar(true)
			return
		}

		setShowCalendar(false)
		const dates = getPresetDates(presetKey)
		if (dates) {
			const desde = getServerDate(dates.desde)
			const hasta = getServerDate(dates.hasta)
			setTempDesde(dates.desde)
			setTempHasta(dates.hasta)
			// Actualizar estado y consultar inmediatamente
			onRangeChange(desde, hasta)
			onConsultar(desde, hasta)
		}
	}

	const handleDateChange = (dates) => {
		const [start, end] = dates
		setTempDesde(start)
		setTempHasta(end)
	}

	const handleConsultarClick = () => {
		setShowCalendar(false)
		if (onConsultar && tempDesde && tempHasta) {
			const desde = getServerDate(tempDesde)
			const hasta = getServerDate(tempHasta)
			onRangeChange(desde, hasta)
			onConsultar(desde, hasta)
		}
	}

	const handleClear = () => {
		const dates = getPresetDates('esteMes')
		setActivePreset('esteMes')
		setTempDesde(dates.desde)
		setTempHasta(dates.hasta)
		setShowCalendar(false)
	}

	return (
		<div className="date-range-selector bg-white rounded shadow-sm p-3 mb-4">
			<div className="d-flex align-items-center flex-wrap gap-2">
				<span className="text-muted me-2">
					<i className="fa fa-calendar me-2"></i>
					Período:
				</span>

				{PRESETS.map(preset => (
					<button
						key={preset.key}
						onClick={() => handlePresetClick(preset.key)}
						className={`btn btn-sm ${activePreset === preset.key ? 'btn-primary' : 'btn-outline-secondary'}`}
					>
						{preset.label}
					</button>
				))}

				<div className="ms-auto position-relative" ref={calendarRef}>
					<button
						className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-2"
						onClick={() => {
							setActivePreset('personalizado')
							setShowCalendar(!showCalendar)
						}}
					>
						<span>{formatDisplayDate(tempDesde || fechaInicio)}</span>
						<span className="text-muted">→</span>
						<span>{formatDisplayDate(tempHasta || fechaFin)}</span>
						<i className="fa fa-calendar-alt ms-2"></i>
					</button>

					{showCalendar && (
						<div
							className="position-absolute end-0 mt-2 bg-white rounded shadow-lg p-3"
							style={{ zIndex: 1000, minWidth: '300px' }}
						>
							<DatePicker
								selected={tempDesde}
								onChange={handleDateChange}
								startDate={tempDesde}
								endDate={tempHasta}
								selectsRange
								inline
								monthsShown={2}
								maxDate={new Date()}
								dateFormat="dd/MM/yyyy"
								locale="es"
							/>
							<div className="d-flex justify-content-end gap-2 mt-2 pt-2 border-top">
								<Button
									variant="outline-secondary"
									size="sm"
									onClick={handleClear}
								>
									Limpiar
								</Button>
								<Button
									variant="primary"
									size="sm"
									onClick={handleConsultarClick}
									disabled={!tempDesde || !tempHasta}
								>
									<i className="fa fa-search me-1"></i>
									Consultar
								</Button>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

export default DateRangeSelector